<template>
  <NuxtLayout name="banner-trip">
    <LazyDetailsTab
      v-if="tripTab === 'details'"
      :itineraries="itineraryArr"
      :trip="trip"
      :is-destination="true"
      :calendar="calendar"
      :suggested="suggested"
    />
    <LazyDeparturesTab
      v-else-if="tripTab === 'calendar'"
      :key="trip?.destination_id"
      :calendar="calendar"
      :flight-trip="!!trip.flight_trip"
      :show-seats-left="false"
    />
  </NuxtLayout>
</template>

<script setup>
definePageMeta({
  scrollToTop: false,
})

const { urls: localeURLs } = useLocale()
const { trackViewContent } = useTracking()
// const { scrollTo } = useScrollMixin()
const { t } = useI18n()
const { $dayjs, $sentrySetContext, $sentryCaptureMessage } = useNuxtApp()
const route = useRoute()
const charterStore = useCharterStore()
const localeStore = useLocaleStore()
const rootStore = useRootStore()
const menuStore = useMenuStore()
const charterPackagesStore = useCharterPackagesStore()
const tripStore = useTripStore()
const router = useRouter()

const { charterAirports } = storeToRefs(rootStore)

const {
  trip,
  itinerary,
  calendar,
  suggestedTriptypes: suggested,
  tripPath,
  tripTab,
} = storeToRefs(tripStore)

const { destination } = storeToRefs(charterStore)

const itineraryArr = computed(() => {
  if (!itinerary.value) {
    return []
  }

  return Object.values(itinerary.value)
})

// const filledSections = computed(() => {
//   return this.sections?.filter(s => !!s.data)
// })

const { error, data } = await useAsyncData(async () => {
  try {
    const tripName = encodeURIComponent(route.params.slug)
    const tripUrlId = `destination/${tripName}`

    const urls = localeStore.urls.tripSlug
    const tab = Object.keys(urls).find(t => urls[t].match(route.params.tab))

    const prevPath = tripPath.value
    const newPath = '/' + tripUrlId
    const isNewTrip = prevPath !== newPath

    tripStore.SET_TRIP_TAB(tab)

    if (isNewTrip) {
      tripStore.INIT_TRIP_VISITED()
      tripStore.SET_TRIP_PATH(newPath)

      const [trip] = await Promise.all([
        charterStore.fetchDestination({ destination: tripName }),
        rootStore.fetchCharterAirports(),
        rootStore.fetchCharterDestinations(),
        charterPackagesStore.getDestinations(),
      ])
      // setup calendar
      const dealfinder = trip.sections.find(s => s.type === 'destination_dealfinder')
      const calendar = {
        departures: [],
        filteroptions: {},
      }

      if (dealfinder && dealfinder.data && dealfinder.data.length > 0) {
        const airports = {}

        const departures = dealfinder.data.flatMap((deals) => {
          airports[deals.airport] = charterAirports.value.find(a => a.airport === deals.airport)?.airport_name

          return deals.triplengths.flatMap((triplength) => {
            return triplength.dates.map((date) => {
              return {
                ...date,
                bookable: true,
                triplength: triplength.name,
                tripname: trip.title + ' ' + date.days + 'd',
                airport_text: airports[deals.airport],
              }
            })
          })
        }).sort((a, b) => $dayjs(a.date).unix() - $dayjs(b.date).unix())

        calendar.departures = departures
        calendar.filteroptions.airports = Object.entries(airports)

        if (dealfinder.data[0].triplengths.length > 0) {
          calendar.filteroptions.triplengths = dealfinder.data[0].triplengths.map(t => t.name)
        }
      }

      const hasDepartures = !!calendar.departures?.length

      const itineraryId = trip.sections.find(s => s.type === 'rolfs_itinerary')?.data

      if (itineraryId) {
        const [itinerary] = await Promise.all([
          tripStore.fetchItinerary(itineraryId),
          tripStore.fetchSuggestedTriptypes(itineraryId),
        ])

        if (!itinerary.error) {
          tripStore.SET_ITINERARY({
            0: {
              ...Object.values(itinerary)[0],
              overviewmap: {
                thumb_bounds: {
                  lat_center: trip.latitude,
                  lng_center: trip.longitude,
                },
                points: [],
              },
            },
          })
        }
      }

      tripStore.SET_TRIP({
        ...trip,
        calendar_type: trip.calendar_type,
        main_picture_cloudinary: trip.image_cloudinary,
        price_min: trip.price_box?.min_price,
        days_name: trip.price_box?.name,
        url: newPath,
        flight_trip: true,
        lat: trip.latitude,
        long: trip.longitude,
        charterSections: trip.sections,
        hasDepartures,
        daysText: !hasDepartures ? null : trip.price_box?.name,
        sections: {
        // hotels_exists: true,
        // faq_exists: false,
        // reviews_exists: true,
        // films_exists: true
        },
      })
      tripStore.SET_CALENDAR(calendar)
      tripStore.SET_TRIP_PATH(newPath)
    }

    const trip = tripStore.trip
    const isDiscounted = trip.price_min < trip.price_min_without_discount

    rootStore.SET_PAGE_BANNER({
      title: trip.title,
      background: {
        image: {
          url: trip.main_picture_cloudinary || trip.image_cloudinary,
        },
      },
      type: 'large',
      stars: !trip.reviews_average ? {} : {
        score: trip.reviews_average.avg,
        reviewCount: trip.reviews_num ?? trip.reviews_average.count,
      },
      link: !trip.hasDepartures ? null : {
        text: t('bookNow'),
      },
      price: trip.price_min,
      priceWithoutDiscount: isDiscounted ? trip.price_min_without_discount : null,
      days: trip.daysText,
    })

    return {
      shouldScrollTop: isNewTrip,
    }
  } catch (e) {
    $sentrySetContext('data', JSON.stringify({
      params: route.params,
      query: route.query,
      exception: e?.data,
    }))
    $sentryCaptureMessage('could not load _sv > _destination > _slug', {
      level: 'warning',
      tags: {
        type: 'UX',
      },
    })
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: 404,
    stack: error.value.stack,
    fatal: true,
  })
}

const { shouldScrollTop } = data.value

useHead({
  title: () => `${destination.value.title}`,
  link: [
    {
      rel: 'canonical',
      href: () => `${localeURLs.BASE_URL}${localeURLs.destinations}/${route.params.slug}/`,
    },
  ],

  meta: [
    { hid: 'author', name: 'author', content: () => t('siteTitleBrand') },
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${localeURLs.destinations}/${route.params.slug}/`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () => {
        const title = `${destination.value.title} - ${t('siteTitleBrand')}`
        return title
      },
    },
    {
      hid: 'twitter:title',
      name: 'twitter:title',
      content: () => `${destination.value.title} - ${t('siteTitleBrand')}`,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: () => formatCloudinaryImagePath(destination.value.image_cloudinary, {
        transformation: { watermark: true },
        forceJPG: true,
        isSol: false,
      }),
    },
    // {
    //   hid: 'og:description',
    //   property: 'og:description',
    //   content: this.sections?.find(s => s.type === 'destination_summary')?.data.preamble
    // }
  ],
})

if (shouldScrollTop?.value && process.browser) {
  window.scrollTo(0, 0)
}

onMounted(() => {
  if (shouldScrollTop?.value) {
    window.scrollTo(0, 0)
  }

  if (trip.value && suggested.value) {
    menuStore.closeSideMenuActive()
  }

  trackViewContent({
    name: trip.value.title,
    value: trip.value.price_min,
    id: trip.value.id,
  })
})

// const scrollToDealfinder = (check = false) => {
//     if (check && !shouldScrollTop.value) {
//       return
//     }
//     const df = document.querySelector('#destination-dealfinder')

//     if (df) {
//       scrollTo(df)
//     }
//   }
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/charter/_charter-destination";

.charter-destination__deal-finder {
  box-shadow: none !important;
  max-width: none;
}
</style>
